var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, DropdownButton, FlexBox, Icon, SelectionList, Spinner, } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import orderBy from 'lodash/orderBy';
import { Fragment, memo, useCallback, useMemo, useRef, useState, } from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { customFieldsApi, } from 'apis/customFields';
import { analytics } from 'app/utils/segment';
import { getFilterAssets } from '../../../../utils/filters/filterAssets';
import { AnalyticsFilterItem, AnalyticsFilterLayout, AnalyticsFilterSearch, } from '../../../filter';
import styles from './analyticsCustomAttributesFilter.module.scss';
import { AnalyticsCustomAttributesFilterSelect } from './components/select/analyticsCustomAttributesFilterSelect';
import { AnalyticsCustomAttributesFilterText } from './components/text/analyticsCustomAttributesFilterText';
export const AnalyticsCustomAttributesFilter = memo(function AnalyticsCustomAttributesFilter({ field, draftControls = [], buttonProps = {}, onChange, }) {
    const { dashboard } = useAnalyticsState();
    const assets = useMemo(() => getFilterAssets(field), [field]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeAttribute, setActiveAttribute] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);
    const searchRef = useRef(null);
    const listWrapperRef = useRef(null);
    const [readAttributesQuery, { attributes, isLoading, isUninitialized }] = customFieldsApi.useLazyReadAttributesQuery({
        selectFromResult: (_a) => {
            var _b;
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { attributes: (_b = data === null || data === void 0 ? void 0 : data.data.attributes) !== null && _b !== void 0 ? _b : [] }));
        },
    });
    // Fetches the custom attributes the first time the dropdown is opened.
    useUpdateEffect(() => {
        if (isOpen && isUninitialized) {
            const preferCacheValue = true;
            readAttributesQuery({ entityId: 'conversation' }, preferCacheValue);
        }
    }, [isOpen, isUninitialized, readAttributesQuery]);
    /**
     * The custom attributes visible to the user.
     *
     * These are first filtered according to the search criteria if any, and
     * finally ordered so that deactivated attributes are at the bottom.
     */
    const visibleAttributes = useMemo(() => {
        const fuse = new Fuse(attributes, {
            keys: ['label', 'identifier'],
            threshold: 0.2,
            distance: 200,
        });
        const filteredAttributes = [
            ...(debouncedSearchTerm
                ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
                : attributes),
        ];
        return orderBy(filteredAttributes, 'is_deactivated', 'asc');
    }, [attributes, debouncedSearchTerm]);
    const handleToggleDropdown = useCallback((newIsOpen) => {
        setIsOpen(newIsOpen);
        if (newIsOpen) {
            analytics.analyticsFilterOpened({
                type: field,
                dashboard: dashboard.title,
            });
            if (listWrapperRef.current) {
                listWrapperRef.current.scrollTop = 0;
            }
        }
        else {
            setActiveAttribute(null);
        }
    }, [dashboard.title, field]);
    const handleChangeActiveAttribute = useCallback(({ selectedItem }) => {
        setActiveAttribute(selectedItem.value);
    }, []);
    /** Returns the draft custom attribute filter control for a given attribute. */
    const getAttributeFilterControl = useCallback((attributeId) => draftControls.find(({ attribute_id }) => attribute_id === attributeId), [draftControls]);
    /** Handles rendering the list of custom attributes. */
    const renderAttributesList = useCallback(() => {
        const isEmpty = !visibleAttributes.length && !isLoading;
        const items = visibleAttributes.map((attribute, index, list) => {
            const isFirstDeactivatedItem = attribute.is_deactivated &&
                (index === 0 || !list[index - 1].is_deactivated);
            const draftControl = getAttributeFilterControl(attribute.id);
            const count = draftControl
                ? draftControl.values.length || 1
                : undefined;
            return (_jsxs(Fragment, { children: [isFirstDeactivatedItem && (_jsx(SelectionList.Divider, { label: "Deactivated" })), _jsxs(AnalyticsFilterItem, Object.assign({ value: attribute, label: attribute.label, highlight: debouncedSearchTerm, noFixedSelectionStateSpace: true, contentClassName: classNames(styles.itemContent, {
                            [styles.itemContentWithBadge]: !!count,
                        }) }, { children: [count && (_jsx(Badge, Object.assign({ variant: "secondary", size: "small" }, { children: count }))), _jsx(Icon, { name: "angle-right" })] }))] }, attribute.identifier));
        });
        return (_jsxs(_Fragment, { children: [_jsx(AnalyticsFilterLayout.Row, Object.assign({ borderBottom: true }, { children: _jsx(AnalyticsFilterSearch, { ref: searchRef, value: searchTerm, isOpen: isOpen, isDisabled: false, onChange: setSearchTerm }) })), _jsxs(AnalyticsFilterLayout.Row, Object.assign({ ref: listWrapperRef, noPadding: true, scrollable: true }, { children: [isLoading && (_jsx(FlexBox, Object.assign({ alignItems: "center", justifyContent: "center", height: "100%", padding: "300" }, { children: _jsx(Spinner, { size: 20 }) }))), _jsxs(SelectionList, Object.assign({ isActive: isOpen, selectedValues: activeAttribute ? [activeAttribute] : undefined, onChange: handleChangeActiveAttribute, equalityFn: (a, b) => a.identifier === b.identifier, defaultActiveItemIndex: 0 }, { children: [items, isEmpty && _jsx(AnalyticsFilterLayout.Empty, {})] }))] }))] }));
    }, [
        activeAttribute,
        debouncedSearchTerm,
        getAttributeFilterControl,
        handleChangeActiveAttribute,
        isLoading,
        isOpen,
        searchTerm,
        visibleAttributes,
    ]);
    /** Handles rendering of the active attribute according to its type. */
    const renderAttributeType = useCallback(() => {
        if (!activeAttribute)
            return null;
        const draftControl = getAttributeFilterControl(activeAttribute.id);
        switch (activeAttribute.type) {
            case 'text':
                return (_jsx(AnalyticsCustomAttributesFilterText, { field: field, attribute: activeAttribute, selectedOperator: draftControl === null || draftControl === void 0 ? void 0 : draftControl.operator, onChange: onChange, onBack: () => setActiveAttribute(null) }));
            case 'select':
                return (_jsx(AnalyticsCustomAttributesFilterSelect, { field: field, attribute: activeAttribute, selectedValues: draftControl === null || draftControl === void 0 ? void 0 : draftControl.values.map(({ value }) => value), selectedOperator: draftControl === null || draftControl === void 0 ? void 0 : draftControl.operator, isDropdownOpen: isOpen, onChange: onChange, onBack: () => setActiveAttribute(null) }));
        }
    }, [activeAttribute, field, getAttributeFilterControl, isOpen, onChange]);
    return (_jsx(DropdownButton, Object.assign({ show: isOpen, dropdown: _jsx(AnalyticsFilterLayout, { children: activeAttribute ? renderAttributeType() : renderAttributesList() }), onToggle: handleToggleDropdown, buttonProps: Object.assign(Object.assign({}, buttonProps), { variant: 'tertiary', disabled: buttonProps.disabled || isLoading }), "data-analytics-filter": field, fixed: true }, { children: assets.label })));
});
